import React, { useEffect, useState } from "react";
import { notify } from "../../components/ui/notify";
import { Spin, Button } from "antd";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { HappyProvider } from "@ant-design/happy-work-theme";

import {
  actionCorresponsalGetById,
  actionCorresponsalCreate,
  actionCorresponsalUpdateById,
} from "../../redux/actions/corresponsales/corresponsales.js";
import "../../components/forms/tribunal/FormTribunal.css";

const collapseSpaces = (s) => String(s ?? "").normalize("NFC").replace(/\s+/g, " ").trim();
const onlyPhoneDigits = (v) => String(v || "").replace(/\D/g, "").slice(0, 15);

export default function CrearCorresponsalPage() {
  const { idCorresponsal } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isEdit = Boolean(idCorresponsal);

  const [loadingEdit, setLoadingEdit] = useState(isEdit);
  const [detalle, setDetalle] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({ nombre: "", correo: "", celular: "" });

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => { const c = { ...e }; delete c[name]; return c; });
  };

  useEffect(() => {
    if (!isEdit) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingEdit(true);
        const data = await dispatch(actionCorresponsalGetById(Number(idCorresponsal)));
        if (!mounted) return;
        setDetalle(data || null);
        setForm({
          nombre: data?.nombre ?? "",
          correo: data?.correo ?? "",
          celular: data?.celular ?? "",
        });
      } catch (e) {
        if (!mounted) return;
        notify.error({
          message: "No se pudo cargar el corresponsal",
          description: e?.response?.data?.detail || e?.message || "Sin detalle",
          placement: "bottomRight",
        });
        setDetalle(null);
      } finally {
        if (mounted) setLoadingEdit(false);
      }
    })();
    return () => { mounted = false; };
  }, [dispatch, isEdit, idCorresponsal]);

  const handleBack = () =>
    navigate(isEdit ? `/corresponsales/${idCorresponsal}` : "/corresponsales");

  const validate = () => {
    const e = {};
    if (!collapseSpaces(form.nombre)) e.nombre = "Campo requerido";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = {
      nombre: collapseSpaces(form.nombre),
      correo: collapseSpaces(form.correo) || null,
      celular: onlyPhoneDigits(form.celular) || null,
    };

    try {
      setSubmitting(true);
      if (isEdit) {
        await dispatch(actionCorresponsalUpdateById(Number(idCorresponsal), payload));
        notify.success({ message: "Corresponsal actualizado", placement: "bottomRight" });
        navigate(`/corresponsales/${idCorresponsal}`);
      } else {
        const res = await dispatch(actionCorresponsalCreate(payload));
        const newId = res?.id;
        notify.success({ message: "Corresponsal creado", placement: "bottomRight" });
        navigate(newId ? `/corresponsales/${newId}` : "/corresponsales");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error al guardar";
      notify.error({ message: "No se pudo guardar", description: msg, placement: "bottomRight" });
    } finally {
      setSubmitting(false);
    }
  };

  const ready = !isEdit || !!detalle;

  return (
    <HappyProvider>
      <div className="oa-exp-page">
        {/* ── TOPBAR ─────────────────────────────────────────────────── */}
        <section className="oa-exp-topbar">
          <div className="oa-exp-topbar-left">
            <div className="oa-exp-title">
              {isEdit ? "Editando Corresponsal" : "Creando Corresponsal"}
            </div>
            <div className="oa-exp-subtitle">Directorio de Corresponsales</div>
          </div>
          <div className="oa-exp-topbar-right">
            <Button className="oa-exp-btn oa-exp-btn-ghost" onClick={handleBack}>
              Cancelar
            </Button>
            <Button
              type="primary"
              className="oa-exp-btn"
              disabled={submitting}
              loading={submitting}
              onClick={handleSubmit}
              icon={
                <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                  <path d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L400 60.7C388 48.7 371.7 42 354.5 42H64zm0 96c0-17.7 14.3-32 32-32H288c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V96zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
                </svg>
              }
            >
              {isEdit ? "Guardar cambios" : "Crear corresponsal"}
            </Button>
          </div>
        </section>

        {/* ── BODY ───────────────────────────────────────────────────── */}
        <main className="oa-exp-main">
          {!ready ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : (
            <section className="oa-card">
              <div className="oa-card-head">
                <h2 className="oa-card-title">
                  <span className="oa-card-icon">
                    <i className="fa-solid fa-user-tie" />
                  </span>
                  DATOS DEL CORRESPONSAL
                </h2>
              </div>

              <div className="oa-card-body oa-grid-3">
                <div className="oa-field">
                  <label className="oa-label">
                    <span className="oa-req">*</span> Nombre
                  </label>
                  <input
                    className={`oa-input ${errors.nombre ? "oa-input-error" : ""}`}
                    type="text"
                    placeholder="Nombre del corresponsal"
                    maxLength={255}
                    value={form.nombre}
                    onChange={(e) => setField("nombre", e.target.value)}
                  />
                  {errors.nombre && <div className="oa-error">{errors.nombre}</div>}
                </div>

                <div className="oa-field">
                  <label className="oa-label">Correo</label>
                  <input
                    className="oa-input"
                    type="email"
                    placeholder="correo@dominio.com"
                    maxLength={150}
                    value={form.correo}
                    onChange={(e) => setField("correo", e.target.value)}
                  />
                </div>

                <div className="oa-field">
                  <label className="oa-label">Celular</label>
                  <input
                    className="oa-input"
                    type="text"
                    placeholder="Ej. 6531234567"
                    maxLength={15}
                    value={form.celular}
                    onChange={(e) => setField("celular", onlyPhoneDigits(e.target.value))}
                  />
                </div>
              </div>
            </section>
          )}
        </main>
      </div>
    </HappyProvider>
  );
}
